import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ProgramFormatService } from '@scripts/woo_services.module/ProgramFormatService';
import { ModalContent } from '@src/shared/modal/confirm-modal/confirm-modal.type';
import { ModalService } from '@src/shared/modal/modal.service';
import { range } from '../../../../utils/array';
import { CreativeService } from '../../../../woo_services.module/CreativeService';
import { FrequencyLimitPrice, PriceList, PriceListService } from '../../../../woo_services.module/PriceListService';
import { ExtraPublisherField, PublisherService } from '../../../../woo_services.module/PublisherService';
import { RoutingService } from '../../../../woo_services.module/RoutingService';
import { PriceListPriceGroups, Program, Publisher, wooId } from '../../../../woo_services.module/shared-types';
import { TranslationService } from '../../../../woo_services.module/TranslationService';
import { ProductsPageUtilityService } from '../../shared/products-page-utility-service';

@Component({
  selector: 'edit-pricelist',
  templateUrl: './edit-pricelist.component.html',
  styleUrls: ['./price-list-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class EditPricelist implements OnInit {
  readonly priceListService = inject(PriceListService);
  readonly creativeService = inject(CreativeService);
  readonly translationService = inject(TranslationService);
  readonly modalService = inject(ModalService);
  readonly utilityService = inject(ProductsPageUtilityService);
  readonly routingService = inject(RoutingService);
  readonly changeDetection = inject(ChangeDetectorRef);
  readonly programFormatService = inject(ProgramFormatService);
  readonly creativeLengths = this.creativeService.getCreativeLengths();
  priceList: Partial<PriceList> = this.priceListService.getEmpty();
  programFormats: Program[];
  selectedProgramFormat: Program;
  newProgramPrice: string;
  publishers: Publisher[] = [];
  basePriceList: PriceRow[] = [];
  targetingPriceList: PriceRow[] = [];
  ageModifierPriceList: PriceRow[] = [];
  localityPriceList: PriceRow[] = [];
  addonTargetingPriceList: PriceRow[] = [];
  weekIndexWeeks: number[][] = [];
  loading = signal<boolean>(true);

  readonly getLabel = this.priceListService.getLabel;
  readonly translateProductFormat = this.translationService.convertProductFormat;
  readonly translateTargetingProduct = this.translationService.convertProductChoice;
  readonly translateAgeModifierKey = this.translationService.convertAgePriceListKey;
  readonly translateLocality = this.translationService.convertLocality;

  @Input() newPriceList?: Partial<PriceList>;
  @Output() onSubmit = new EventEmitter<Omit<PriceList, 'id'>>();

  constructor(private publisherService: PublisherService, private route: ActivatedRoute) {}

  async ngOnInit(): Promise<void> {
    try {
      const [publishers, programFormats] = await Promise.all([
        this.publisherService.getPublishers(ExtraPublisherField.enabled),
        this.programFormatService.getProgramFormats(),
      ]);

      this.publishers = publishers.filter((p) => p.enabled);
      this.programFormats = programFormats;

      await this.reloadPriceListAndUpdateCompnent();
    } finally {
      this.changeDetection.markForCheck();
      this.loading.set(false);
    }
  }

  async updatePriceList(): Promise<void> {
    const list = this.priceList as PriceList;
    const dialogContent: ModalContent = {
      title: 'Uppdatera prislista',
      texts: [`Är du säker på att du vill uppdatera hela prislistan för ${this.priceListService.getLabel(list)}?`],
      confirmLabel: 'Uppdatera',
      cancelLabel: 'Avbryt',
    };
    const response = await this.modalService.openConfirm(dialogContent);

    if (!response.confirm) return;

    list.id = this.routingService.getParam(this.route, 'id');
    await this.priceListService.updatePriceList(list);

    const msg = `Prislistan för ${this.priceListService.getLabel(list)} har uppdaterats`;
    this.modalService.openSuccess(msg);

    this.changeDetection.markForCheck();
    this.goToProducts();
  }

  async addProgramPrice(): Promise<void> {
    const dialogContent: ModalContent = {
      title: 'Lägg till programspecifikt pris',
      texts: ['Är du säker på att du vill lägga till nytt pris?'],
      confirmLabel: 'Lägg till',
      cancelLabel: 'Avbryt',
    };

    const response = await this.modalService.openConfirm(dialogContent);
    if (!response.confirm) return;

    const priceListId = this.routingService.getParam(this.route, 'id');
    await this.priceListService.addProgramSpecificPrice(
      priceListId,
      this.selectedProgramFormat.id,
      this.newProgramPrice,
    );

    this.modalService.openSuccess('Priset har lagts till!');

    this.loading.set(true);
    await this.reloadPriceListAndUpdateCompnent();
    this.loading.set(false);
  }

  get priceListHeading(): string {
    return `Prislista för år ${this.getLabel(this.priceList as PriceList)}`;
  }

  get overWrittenHeadingText(): string {
    return `Redigerade priser (${Object.keys(this.priceList.overwritten_product_prices).length} st)`;
  }

  get showOverWrittenPriceComponent(): boolean {
    return !this.newPriceList || !!Object.keys(this.priceList.overwritten_product_prices)?.length;
  }

  get saveButtonLabel(): string {
    return `${this.newPriceList ? 'Skapa' : 'Spara'} prislista`;
  }

  get priceHeadingLabel(): string {
    return 'CPT/CPM';
  }

  goToProducts(): void {
    this.routingService.navigateWithReload(['products'], {
      queryParams: { tab: 'prislistor' },
    });
  }

  getActivePrices(frequencyLimitPrices?: FrequencyLimitPrice[]): FrequencyLimitPrice[] {
    return (frequencyLimitPrices || []).filter((price) => price.frequency_limit.active);
  }

  getPublisherName(publisherId: wooId): string {
    return this.publishers.find((publisher) => publisher.id === publisherId)?.name;
  }

  reloadPriceList(priceListId: wooId): void {
    if (this.priceList.id === priceListId) {
      this.loading.set(true);
      this.reloadPriceListAndUpdateCompnent().finally(() => {
        this.loading.set(false);
      });
    }
  }

  clearOverwrittenPrices(): void {
    if (this.newPriceList) {
      this.priceList.overwritten_product_prices = {};
    }
  }

  disableAddProgramPrice(): boolean {
    return !this.selectedProgramFormat || !this.newProgramPrice;
  }

  private reloadPriceListAndUpdateCompnent(): Promise<void> {
    this.selectedProgramFormat = null;
    this.newProgramPrice = null;
    return this.loadPriceList().then(() => {
      this.utilityService.filterDisabledPublisherPrices(this.priceList, this.publishers);
      this.basePriceList = this.buildPriceRows(PriceListPriceGroups.publisher_base_prices);
      this.targetingPriceList = this.buildPriceRows(PriceListPriceGroups.publisher_targeting_prices);
      this.ageModifierPriceList = this.buildPriceRows(PriceListPriceGroups.publisher_age_modifier_prices);
      this.localityPriceList = this.buildPriceRows(PriceListPriceGroups.publisher_locality_prices);
      this.addonTargetingPriceList = this.buildPriceRows(PriceListPriceGroups.publisher_addon_targeting_prices);
      const weeks = this.priceListService.getValidWeekCount(this.priceList as PriceList);
      const firstWeeks = Math.ceil(weeks / 2);
      const secondWeeks = Math.floor(weeks / 2);
      this.weekIndexWeeks = [range(firstWeeks, 1), range(secondWeeks, firstWeeks + 1)];
    });
  }

  private loadPriceList(): Promise<void> {
    if (this.newPriceList) {
      this.priceList = this.newPriceList;
      return Promise.resolve();
    }

    return this.priceListService.getPriceList(this.routingService.getParam(this.route, 'id')).then((priceList) => {
      this.priceList = priceList;
    });
  }

  private buildPriceRows(priceGroup: string): PriceRow[] {
    let priceRows: PriceRow[] = [];

    Object.keys(this.priceList[priceGroup]).map((publisherId) => {
      Object.keys(this.priceList[priceGroup][publisherId]).map((priceType) => {
        priceRows = [
          ...priceRows,
          {
            product: priceType,
            publisherId: publisherId,
            price: this.priceList[priceGroup][publisherId][priceType],
          },
        ];
      });
    });
    return priceRows;
  }
}

interface PriceRow {
  product: string;
  publisherId: wooId;
  price: number;
}
